import { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  signal,
} from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import { HrAssistantAnalyticsService } from '@core/intelligence-logic';
import { HrAssistantTriggerComponent } from '@core/intelligence-ui';
import {
  type FeatureTooltipDirectiveConfig,
  FeatureTooltipWrapperDirective,
} from '@core/product-updates-legacy';
import { TellentBrand } from '@core/tellent-types';
import { ConnectedOverlayDirective, OverlayOriginDirective } from '@core/user-interface-legacy';
import { ComponentPositionPair, ComponentTooltipPosition } from '@core/user-interface-types';

import { HrAssistantService } from './hr-assistant.service';
import { HrAssistantChatComponent } from '../hr-assistant-chat/hr-assistant-chat.component';

/**
 * @deprecated Use `<tellent-assistant>` (gated by `TellentDevFlag.TellentAssistant`).
 * This component renders when the flag is OFF and for apps that haven't migrated.
 * Do not add features here — see `libs/tellent-assistant/` and canonical docs at
 * `tellent-intelligence/docs/core/`.
 */
@Component({
  selector: 'hr-assistant',
  imports: [
    HrAssistantTriggerComponent,
    HrAssistantChatComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    FeatureTooltipWrapperDirective,
  ],
  templateUrl: './hr-assistant.component.html',
  styleUrl: './hr-assistant.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HrAssistantComponent {
  private readonly hrAssistantService = inject(HrAssistantService);
  private readonly hrAssistantAnalytics = inject(HrAssistantAnalyticsService);
  private readonly injector = inject(Injector);
  private readonly i18n = inject(I18nService);

  protected readonly featureTooltipUsed = this.hrAssistantService.featureTooltipUsed;
  protected readonly positions = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ] satisfies ConnectionPositionPair[];
  protected readonly userData = this.hrAssistantService.userData;

  protected chatOpen = signal(false);
  protected assistantEntitled = this.hrAssistantService.assistantEntitled;
  protected assistantConfig = this.hrAssistantService.assistantConfig;
  protected assistantEnabled = computed(() => {
    const entitled = this.assistantEntitled();
    if (!entitled) return false;

    const config = this.assistantConfig();
    return config ? config.status === 'enabled' : false;
  });

  protected showFeatureTooltip = signal(false);
  protected hrAssistantFeatureTooltipEnabled = computed(() => {
    const config = this.assistantConfig();
    if (!config || this.featureTooltipUsed()) return false;

    return (config.ask.enabled || config.wiki.enabled) && this.showFeatureTooltip();
  });
  protected hrAssistantFeatureTooltip = computed((): FeatureTooltipDirectiveConfig => {
    const config = this.assistantConfig();
    const partialConfig = {
      id: 'hr-assistant-feature-tooltip',
      ctaText: this.i18n.translate('tlc.hr_assistant.feature_tooltip.got_it'),
      badgeCopy: this.i18n.translate('tlc.common.new'),
      isNew: false,
      brand: TellentBrand.Tellent,
      positions: {
        [ComponentTooltipPosition.Left]: new ComponentPositionPair(
          { originX: 'start', originY: 'center' },
          { overlayX: 'end', overlayY: 'center' },
          'right',
          0,
          0,
        ),
      },
      title: this.i18n.translate('tlc.hr_assistant.feature_tooltip.title'),
      description: '',
    } satisfies Partial<FeatureTooltipDirectiveConfig>;
    if (!config || (!config.ask.enabled && !config.wiki.enabled)) return partialConfig;

    if (config.ask.enabled && config.wiki.enabled) {
      return {
        ...partialConfig,
        description: this.i18n.translate('tlc.hr_assistant.feature_tooltip.desc_ask_wiki'),
      };
    } else if (config.ask.enabled) {
      return {
        ...partialConfig,
        description: this.i18n.translate('tlc.hr_assistant.feature_tooltip.desc_ask'),
      };
    } else {
      return {
        ...partialConfig,
        description: this.i18n.translate('tlc.hr_assistant.feature_tooltip.desc_wiki'),
      };
    }
  });

  public ngOnInit() {
    this.hrAssistantService.openWalkthrough();
  }

  protected trackTriggerClick() {
    this.hrAssistantAnalytics.trackTriggerClick();
  }

  protected toggleChat() {
    this.chatOpen.update(open => !open);
    if (this.chatOpen()) {
      afterNextRender(() => this.showFeatureTooltip.set(true), { injector: this.injector });
    }
  }

  protected onFeatureTooltipDismissed() {
    this.hrAssistantService.markHrAssistantTooltipUsed();
  }
}
